import {
  isWeb,
  Paragraph,
  shadowStandard,
  SizableText,
  SmartLink,
  XStack,
  YStack,
  Stack,
} from '@my/ui'
import { LinearGradient } from '@tamagui/linear-gradient'
import { FeaturedOfferBlock } from './featured-offer.block'
import { OfferEndTimeTag } from '../featured-offers-block/components/offer-end-time-tag'
import { RedeemOfferButton, SaveOfferButton } from '../../containers'
import { SolitoImage } from 'solito/image'

export const FeaturedOfferComponent = (props: FeaturedOfferBlock) => {
  if (!props.offer) return null

  if (props.withBackgroundImage) {
    return (
      <YStack
        flex={1}
        br="$3"
        overflow="hidden"
        position="relative"
        minHeight={358}
        $gtSm={{ minHeight: 500 }}
      >
        <XStack zIndex="$2" position="absolute" top="$3.5" right="$3.5">
          <SaveOfferButton
            size={32}
            offerId={props.offer.id}
            isBookmarked={props.offer.isBookmarked}
          />
        </XStack>

        <Stack pos="absolute" flex={1} overflow="hidden" w="100%" h="100%">
          <SolitoImage
            src={props.offer.featuredImage?.url}
            alt={props.offer.featuredImage?.alt}
            contentFit="cover"
            fill
            sizes="(max-width: 900px) 100vw, 892px"
            priority={props.imagePriority}
          />
        </Stack>

        <SmartLink
          Component={YStack}
          href={`/discounts/${props.offer.brand.slug}/${props.offer.slug}`}
          flex={1}
          hoverStyle={{
            opacity: 0.95,
          }}
          pressStyle={{
            opacity: 0.85,
          }}
        >
          {isWeb ? (
            <div
              style={{
                backgroundImage: 'linear-gradient(0deg, rgba(0,0,0,0.7), rgba(0,0,0,.07))',
                position: 'absolute',
                width: '100%',
                height: '100%',
              }}
            />
          ) : (
            <LinearGradient
              width="100%"
              height="100%"
              position="absolute"
              bottom="$0"
              left="$0"
              colors={['$overlay13', '$overlay0']}
              start={[0, 1]}
              end={[0, 0]}
            />
          )}

          <YStack
            aspectRatio={1}
            m="$3.5"
            br="$2"
            bc="$white"
            w={44}
            h={44}
            boc="$white"
            bw="$1.5"
            $gtSm={{ w: 88, h: 88, bw: '$2' }}
            {...shadowStandard}
          >
            <SolitoImage
              src={props.offer.brand.logo.url}
              alt={props.offer.brand.logo.alt}
              fill
              sizes="(max-width: 576px) 44px, 88px"
              contentFit="contain"
              style={{ borderRadius: 4, backgroundColor: 'white' }}
            />
          </YStack>

          <YStack mt="auto" gap="$2" p="$3.5" $gtSm={{ p: '$6' }}>
            <SizableText
              color="$white"
              size="$6"
              fow="800"
              numberOfLines={2}
              zIndex="$1"
              $gtSm={{
                size: '$9',
                fow: '800',
              }}
            >
              {props.offer.name}
            </SizableText>
            {props.offer.description && (
              <SizableText
                size="$4"
                numberOfLines={2}
                color="$white"
                $gtSm={{
                  size: '$7',
                }}
              >
                {props.offer.description}
              </SizableText>
            )}

            {props.showEndTime && props.offer.expiresAt && (
              <OfferEndTimeTag expiresAt={props.offer.expiresAt} alignSelf="flex-start" mt="$2.5" />
            )}
          </YStack>
        </SmartLink>
      </YStack>
    )
  }

  return (
    <YStack position="relative" flex={1} height="100%">
      <XStack zIndex={10} position="absolute" top="$3.5" right="$3.5">
        <SaveOfferButton
          size={32}
          offerId={props.offer.id}
          isBookmarked={props.offer.isBookmarked}
        />
      </XStack>

      <YStack br="$3" overflow="hidden" bg="$white" flex={1} {...shadowStandard}>
        <YStack flex={1} $gtMd={{ flexDirection: 'row-reverse' }}>
          <SmartLink
            Component={YStack}
            tabIndex={-1}
            aria-label="View offer"
            href={`/discounts/${props.offer.brand.slug}/${props.offer.slug}`}
            flex={1}
            $gtMd={{ width: '50%' }}
            pressStyle={{
              opacity: 0.85,
            }}
            hoverStyle={{
              opacity: 0.95,
            }}
          >
            {props.offer.featuredImage && (
              <Stack
                overflow="hidden"
                aspectRatio={1}
                $gtXs={{ aspectRatio: 16 / 9 }}
                $gtMd={{ aspectRatio: 1 }}
              >
                <SolitoImage
                  src={props.offer.featuredImage.url}
                  alt={props.offer.featuredImage.alt || props.offer.name}
                  sizes="(max-width: 900px) 100vw, 446px"
                  fill
                  contentFit="cover"
                />
              </Stack>
            )}
          </SmartLink>

          <YStack
            flex={1}
            mt="$-10"
            gap="$3.5"
            p="$3.5"
            $gtSm={{ p: '$6' }}
            $gtMd={{ width: '50%', mt: 0 }}
          >
            <SmartLink
              Component={Stack}
              href={`/discounts/${props.offer.brand.slug}/${props.offer.slug}`}
              flexGrow={1}
              pressStyle={{
                opacity: 0.9,
              }}
              hoverStyle={{
                opacity: 0.95,
              }}
            >
              <YStack
                mb="$3.5"
                mx="auto"
                br="$2"
                bc="$white"
                boc="$white"
                bw="$2"
                alignSelf="flex-start"
                $gtMd={{ m: 0, mb: '$3.5' }}
                {...shadowStandard}
              >
                <SolitoImage
                  src={props.offer.brand.logo.url}
                  alt={props.offer.brand.logo.alt || ''}
                  contentFit="contain"
                  width={88}
                  height={88}
                  style={{ borderRadius: 8, backgroundColor: 'white' }}
                />
              </YStack>

              <SizableText
                mt="auto"
                numberOfLines={3}
                size="$6"
                fow="800"
                ta="center"
                $gtXs={{ size: '$8', fow: '800' }}
                $gtMd={{ ta: 'left' }}
              >
                {props.offer.name}
              </SizableText>
              <Paragraph
                size="$3"
                numberOfLines={3}
                color="$overlay11"
                pt="$3"
                ta="center"
                $gtXs={{ size: '$5' }}
                $gtMd={{ ta: 'left' }}
              >
                {props.offer.description}
              </Paragraph>
            </SmartLink>

            {props.offer.redeemInList && (
              <RedeemOfferButton
                offer={props.offer}
                isEmbed={props.isEmbedded}
                buttonProps={{
                  h: 48,
                  $gtSm: {
                    maxWidth: '100%',
                  },
                }}
                $gtSm={{
                  mt: 'auto',
                }}
              />
            )}
          </YStack>
        </YStack>
      </YStack>
    </YStack>
  )
}
